import { useEffect } from 'react';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import type { ProjectContext as ProjectContextType } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { BUSINESS } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/constants.tsx';
import { useJsmOnboardingType } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/main.tsx';
import { useSpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export const useQuickstart = (
	projectContextData: ProjectContextType | null | undefined,
	containerType: 'project' | 'plan' = 'project',
): boolean => {
	const [{ data: containerContext }] = useContainerContext();
	const [{ isReady: spaStatePageReady }] = useSpaStatePageReady();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [
		jsmOnboardingData,
		fetchOnboardingType,
		projectContextData?.projectId,
		projectContextData?.projectType,
	]);

	if (
		(!fg('quickstart_onboarding_in_jira_plans') || containerType === 'project') &&
		projectContextData?.projectType !== SOFTWARE_PROJECT &&
		projectContextData?.projectType !== CORE_PROJECT &&
		projectContextData?.projectType !== SERVICE_DESK_PROJECT
	) {
		// If containerType is project and projectContext is present and it is not software, core or service management, don't render
		return false;
	}

	// Scope Type in classic -- scrum/kanban/undefined(non-scrum/kanban) & next-gen -- simple
	const isProjectScopeAvailable =
		containerContext && containerContext.scope && containerContext.scope.type;

	const isProjectScopeAndSpaPageReady = Boolean(isProjectScopeAvailable) && spaStatePageReady;

	const showJwmExperience =
		containerContext?.contextType === 'project' && projectContextData?.projectType === CORE_PROJECT;

	const showJsmExperience =
		containerContext?.contextType === 'project' &&
		projectContextData?.projectType === SERVICE_DESK_PROJECT &&
		jsmOnboardingData?.onboardingType === BUSINESS;

	const showPlanExperience = containerType === 'plan';

	const canShowExperience =
		showJwmExperience || showJsmExperience || isProjectScopeAndSpaPageReady || showPlanExperience;

	return canShowExperience;
};
